<template>
  <base-agent-list
    :avatar="avatar"
    :agents="agents"
    :actions="primary"
    :actions-type="actionsType"
    :rule-groups="ruleGroups"
    :statuses="statuses"
    :size="size"
    :default-search-options="defaultSearchOptions"
    :can-change-search-options="canChangeSearchOptions"
    :is-loading="isLoading"
    :is-disabled="isDisabled"
    @search-change="search = $event"
    @action-click="onActionClick"
  >
    <template #options-append>
      <base-item
        v-if="canInviteThirdParty && isSearchValidPhoneNumber"
        :title="search"
        :description="normalizedPhoneNumber"
        :size="size"
        :actions="secondary"
        :actions-type="actionsType"
        :is-disabled="isDisabled"
        @action-click="onActionClick({ key: $event })"
      />
    </template>
  </base-agent-list>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';

import { BaseAgentList, BaseItem } from '@/modules/agent-picker';
import { IAgent } from '@/types';
import {
  AVATAR_PROP,
  SIZE_PROP,
  LIST_ITEM_ACTIONS_DISPLAY_PROP,
  AGENT_ACTIONS,
  RULE_GROUPS_PROP,
  AGENT_ACTIONS_TYPE_PROP,
} from '../../constants';

export default defineComponent({
  name: 'AppDialerAgentList',
  components: {
    BaseAgentList,
    BaseItem,
  },
  props: {
    avatar: AVATAR_PROP,
    agents: {
      type: Array as PropType<IAgent[]>,
      default: () => [],
    },
    actionsType: LIST_ITEM_ACTIONS_DISPLAY_PROP,
    /** Difference between "Invite" and "Transfer" is mostly UI only */
    agentActionsType: AGENT_ACTIONS_TYPE_PROP,
    ruleGroups: RULE_GROUPS_PROP,
    size: SIZE_PROP,
    statuses: { type: Array, default: () => [] },
    defaultSearchOptions: { type: Object, default: undefined },
    canChangeSearchOptions: Boolean,
    isLoading: Boolean,
    isDisabled: Boolean,
    canInviteThirdParty: Boolean,
  },
  emits: ['invite-agent', 'invite-3rd-party'],
  data() {
    return {
      search: '',
    };
  },
  computed: {
    primary() {
      const LABELS = {
        'invite': this.$t('hitl.modules.dialer.actions.add-agent-to-call'),
        'transfer': this.$t('hitl.modules.dialer.actions.transfer-to-agent'),
      };
      return [{
        key: AGENT_ACTIONS.ADD_AGENT,
        label: LABELS[this.agentActionsType],
      }];
    },

    secondary() {
      const LABELS = {
        'invite': this.$t('hitl.modules.dialer.actions.add-3rd-party-to-call'),
        'transfer': this.$t('hitl.modules.dialer.actions.transfer-to-3rd-party'),
      };
      return [{
        key: AGENT_ACTIONS.ADD_3RD_PARTY,
        label: LABELS[this.agentActionsType],
      }];
    },

    /* Input like "+123 (456) 789" will be computed as a number "+123456789" */
    normalizedPhoneNumber(): string {
      return this.search.replace(/[^+\d]/gm, '');
    },

    isSearchValidPhoneNumber(): boolean {
      const MIN_NUMBER_LENGTH = 3;
      const MAX_NUMBER_LENGTH = 16;

      const length = this.normalizedPhoneNumber.length - 1;
      return length >= MIN_NUMBER_LENGTH && length <= MAX_NUMBER_LENGTH;
    },
  },
  methods: {
    onActionClick(args: { agent?: IAgent; key: string; }) {
      if (args.key === AGENT_ACTIONS.ADD_AGENT) {
        this.$emit('invite-agent', { agentId: args.agent!.id });
        return;
      }

      if (args.key === AGENT_ACTIONS.ADD_3RD_PARTY) {
        const data = { identifier: this.normalizedPhoneNumber };
        this.$emit('invite-3rd-party', data);
      }
    },
  },
});
</script>
